import { createAsyncThunk, createSlice } from '@reduxjs/toolkit'
import { AxiosResponse } from 'axios'
import { toast } from 'react-toastify'
import {
  ICoverageCellResponse,
  ICoverageMatrixResponse,
  ICoverageModel,
} from 'types/api/coverageApi.interface'
import { ICoverageSlice, ISaveCoverageModelsPayload } from 'types/coverage.interface'
import { baseUrl } from 'utils/axios'
import { handleErrors } from 'utils/helpers'
import {
  buildDemoCell,
  buildDemoMatrix,
  demoModelsFor,
  saveDemoModelVersions,
  syncCoverageRegistries,
  USE_COVERAGE_MOCKS,
} from 'pages/Shifts/Coverage/coverageDemoStore'

const syncRegistry = (thunkAPI: any) => syncCoverageRegistries(thunkAPI.getState())

const initialState: ICoverageSlice = {
  isLoading: false,
  hasError: false,
  matrix: null,
  cell: null,
  models: [],
}

export const getCoverageMatrix = createAsyncThunk(
  'coverage/getMatrix',
  async (params: any, thunkAPI) => {
    if (USE_COVERAGE_MOCKS) {
      syncRegistry(thunkAPI)
      return buildDemoMatrix(params.siteNodeId || 1, params.date)
    }
    try {
      const resp: AxiosResponse<ICoverageMatrixResponse> = await baseUrl.get(
        'v1/shift-coverage/matrix',
        { params },
      )
      return resp.data
    } catch (error) {
      return thunkAPI.rejectWithValue(error)
    }
  },
)

export const getCoverageCell = createAsyncThunk(
  'coverage/getCell',
  async (params: any, thunkAPI) => {
    if (USE_COVERAGE_MOCKS) {
      syncRegistry(thunkAPI)
      return buildDemoCell(params.siteNodeId || 1, params.departmentId, params.shiftId, params.date)
    }
    try {
      const resp: AxiosResponse<ICoverageCellResponse> = await baseUrl.get('v1/shift-coverage/cell', {
        params,
      })
      return resp.data
    } catch (error) {
      return thunkAPI.rejectWithValue(error)
    }
  },
)

export const getCoverageModels = createAsyncThunk(
  'coverage/getModels',
  async (params: any, thunkAPI) => {
    if (USE_COVERAGE_MOCKS) {
      syncRegistry(thunkAPI)
      return demoModelsFor(params.siteNodeId || 1, params.departmentId)
    }
    try {
      const resp: AxiosResponse<ICoverageModel[]> = await baseUrl.get('v1/coverage-models', { params })
      return resp.data
    } catch (error) {
      return thunkAPI.rejectWithValue(error)
    }
  },
)

export const saveCoverageModels = createAsyncThunk(
  'coverage/saveModels',
  async (payload: ISaveCoverageModelsPayload, thunkAPI) => {
    if (USE_COVERAGE_MOCKS) {
      syncRegistry(thunkAPI)
      const created = saveDemoModelVersions(payload)
      return { created }
    }
    try {
      const resp: AxiosResponse<{ created: number }> = await baseUrl.post('v1/coverage-models', payload)
      return resp.data
    } catch (error) {
      return thunkAPI.rejectWithValue(error)
    }
  },
)

const coverageSlice = createSlice({
  name: 'coverage',
  initialState,
  reducers: {
    clearCoverageCell: (state) => {
      state.cell = null
    },
  },
  extraReducers: {
    [getCoverageMatrix.pending.type]: (state) => {
      state.isLoading = true
      state.hasError = false
    },
    [getCoverageMatrix.fulfilled.type]: (
      state: ICoverageSlice,
      { payload }: { payload: ICoverageMatrixResponse },
    ) => {
      state.isLoading = false
      state.matrix = payload
    },
    [getCoverageMatrix.rejected.type]: (state, action) => {
      state.isLoading = false
      state.hasError = true
      handleErrors(action.payload)
    },
    [getCoverageCell.fulfilled.type]: (
      state: ICoverageSlice,
      { payload }: { payload: ICoverageCellResponse | null },
    ) => {
      state.cell = payload
    },
    [getCoverageCell.rejected.type]: (state, action) => {
      handleErrors(action.payload)
    },
    [getCoverageModels.fulfilled.type]: (
      state: ICoverageSlice,
      { payload }: { payload: ICoverageModel[] },
    ) => {
      state.models = payload
    },
    [getCoverageModels.rejected.type]: (state, action) => {
      handleErrors(action.payload)
    },
    [saveCoverageModels.fulfilled.type]: (state, { payload }: { payload: { created: number } }) => {
      toast.success(payload.created ? `Coverage model saved (${payload.created} shift${payload.created === 1 ? '' : 's'} updated)` : 'No changes to save')
    },
    [saveCoverageModels.rejected.type]: (state, action) => {
      handleErrors(action.payload)
    },
  },
})

export const { clearCoverageCell } = coverageSlice.actions
export default coverageSlice.reducer
